/**
 * Created by tschenkel on 7/7/2015.
 */
import { LocalizationModuleError } from "../errors/LocalizationModuleError";
import { ILocalizationService } from "../service/api/ILocalizationService";
import { LanguageVO } from "../vos/LanguageVO";
import { LanguageData } from "../parsers/LanguageMetaData";

import { getLogger } from "@goodgamestudios/logger";

const logger = getLogger("LocalizationJS.Localization");

/**
 * <p>
 *     <strong>Localization</strong>
 * </p>
 *
 * <p>
 *     The <code>Localization</code> class is a wrapper for the <code>ILocalizationService</code> interface and is
 *     intended to be used when there is not possibility to get the localization setService through injection.
 *     This class only expose all methods from the interface, allowing direct access to them.
 * </p>
 *
 * <p>
 *     Before use this class, have sure that the module was already installed and configured properly into game
 *     context. Otherwise this class will throw a error.
 * </p>
 *
 * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService
 *
 */
export class Localization {
    /**
     * @private
     */
    private static _service: ILocalizationService;

    public static CODE_LOCALIZATION_SERVICE_NOT_ASSIGNED: string = "001";
    public static MESSAGE_LOCALIZATION_SERVICE_NOT_ASSIGNED: string =
        "Localization Service was not assigned to Static Localization Helper (use setService to do so)";

    /**
     * @private
     */
    private static assertServiceAlreadyInstalled(): void {
        if (Localization._service == null) {
            throw new LocalizationModuleError(
                Localization.MESSAGE_LOCALIZATION_SERVICE_NOT_ASSIGNED,
                Localization.CODE_LOCALIZATION_SERVICE_NOT_ASSIGNED
            );
        }
    }

    /**
     * @private
     * @param localizeService
     */
    public static setService(localizationService: ILocalizationService): void {
        if (Localization._service != null) {
            logger.warn(
                "LocalizeService was already setup for static Localize - it is going to be overridden!  hopefully you know what you are doing"
            );
        }
        Localization._service = localizationService;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#initialize()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#initialize()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static initialize(languageVO: LanguageVO, texts: any, cldrJSONData?: any, usePipe: boolean = false): void {
        // Localization.assertServiceAlreadyInstalled();
        if (Localization._service == null) {
            throw new LocalizationModuleError(
                Localization.MESSAGE_LOCALIZATION_SERVICE_NOT_ASSIGNED,
                Localization.CODE_LOCALIZATION_SERVICE_NOT_ASSIGNED
            );
        }
        Localization._service.initialize(languageVO, texts, cldrJSONData, usePipe);
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#finalize()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#finalize()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static finalize(): void {
        Localization.assertServiceAlreadyInstalled();

        Localization._service.finalize();
    }

    public static setTexts(texts: LanguageData): void {
        Localization.assertServiceAlreadyInstalled();
        Localization._service.setTexts(texts);
    }


    /**
     * Return if the localization setService was already installed.
     */
    public static get installed(): boolean {
        return Localization._service != null;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#initialized
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#initialized
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:Locali
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get initialized(): boolean {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.initialized;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#usePipe
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#usePipe
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get usePipe(): boolean {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.usePipe;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#usePipe
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#usePipe
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static set usePipe(value: boolean) {
        Localization.assertServiceAlreadyInstalled();

        Localization._service.usePipe = value;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#pipe
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#pipe
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get pipe(): string {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.pipe;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#localizeReplacements
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#localizeReplacements
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get localizeReplacements(): boolean {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.localizeReplacements;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#localizeReplacements()
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#localizeReplacements()
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static set localizeReplacements(value: boolean) {
        Localization.assertServiceAlreadyInstalled();

        Localization._service.localizeReplacements = value;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#locale
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#locale
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get locale(): string {
        Localization.assertServiceAlreadyInstalled();
        return Localization._service.locale;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#language
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#language
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get language(): string {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.language;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#timeZone
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#timeZone
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get timeZone(): string {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.timeZone;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#languageDataVersion
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#languageDataVersion
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get languageXMLVersion(): string {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.languageDataVersion;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#languageDataTimestamp
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#languageDataTimestamp
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get languageXMLTimeStamp(): string {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.languageDataTimestamp;
    }

    /**
     * @copy com.goodgamestudios.core.localization.setService.api.ILocalizationService#processorType
     * @see com.goodgamestudios.core.localization.setService.api.ILocalizationService#processorType
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_LOCALIZATION_MODULE_NOT_INSTALLED</code>:
     * Localization Module was not installed on game context. You have to install the module first.
     */
    public static get processorType(): string {
        Localization.assertServiceAlreadyInstalled();

        return Localization._service.processorType;
    }
}
